import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getExamQuizzes,
  getExamSubjects,
  updateExamQuiz,
} from '../../../api/testCenter.api';
import useAxios from '../../../hooks/useAxios';
import {
  IGetExamQuizzes,
  IGetExamSubjects,
} from '../../../models/testCenter.model';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Spinner';
import { Select, Table, TableColumnsType } from 'antd';
import useNotification from '../../../hooks/useNotification';

interface IProps {
  examCode: number | undefined;
  isPublished: boolean;
}

interface IQuiz {
  quizCode: number;
  quizSeq: number;
  quizTime: number;
  quizAnswer: string;
  scorePerQuiz: number;
}

const TestQuiz: FC<IProps> = ({ examCode, isPublished }) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetExamSubjects>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetExamQuizzes>();
  const {
    data: updateRes,
    isLoading: updateIsLoading,
    error: updateError,
    execute: updateExecute,
  } = useAxios();

  const [examSubjectCode, setExamSubjectCode] = useState<number | undefined>();
  const [quizData, setQuizData] = useState<IQuiz[]>([]);
  const [quizTimes, setQuizTimes] = useState<string[]>([]);

  useEffect(() => {
    if (examCode) {
      setExamSubjectCode(undefined);
      setQuizData([]);
      setQuizTimes([]);
      execute(getExamSubjects(examCode));
    }
  }, [examCode]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      setQuizData(res2.data[0]?.quiz);
      setQuizTimes(res2.data[0]?.quiz?.map((item) => String(item.quizTime)));
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (updateRes && !updateIsLoading) {
      successNotification(t('common.message.save1'));
    }
  }, [updateRes, updateIsLoading]);

  const onSetSubject = (value: number) => {
    if (!examCode) return;

    setExamSubjectCode(value);
    execute2(getExamQuizzes(examCode, value));
  };

  const getTotal = (data: IQuiz[]) => {
    let total = 0;
    data.forEach((item) => {
      total += item.scorePerQuiz;
    });
    if (total > 100) total = 100;

    return total ? Math.round(total) : '-';
  };

  const columns: TableColumnsType<IQuiz> = [
    {
      title: t('cbt.test.column18'),
      dataIndex: 'quizSeq',
      key: 'quizSeq',
      align: 'center',
      width: '6%'
    },
    {
      title: t('cbt.test.column17'),
      dataIndex: 'quizCode',
      key: 'quizCode',
      align: 'center',
      width: '10%'
    },
    {
      title: t('cbt.test.column19'),
      dataIndex: 'quizAnswer',
      key: 'quizAnswer',
      align: 'center',
      width: '6%'
    },
    {
      title: t('cbt.test.column20'),
      dataIndex: 'quizTime',
      key: 'quizTime',
      align: 'center',
      width: 110
    },
    {
      title: 'Skill',
      dataIndex: 'skillName',
      key: 'skillName',
      align: 'center',
      ellipsis: true,
      render: value => <div className="text-left ant-table-cell-ellipsis">{value}</div>,
    },
    {
      title: 'Sub Skill',
      dataIndex: 'subSkillName',
      key: 'subSkillName',
      align: 'center',
      ellipsis: true,
      render: value => <div className="text-left ant-table-cell-ellipsis">{value}</div>,
    },
    {
      title: t('cbt.test.column21'),
      dataIndex: 'scorePerQuiz',
      key: 'scorePerQuiz',
      align: 'center',
      width: '6%'
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || updateIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={updateError} />

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('cbt.type.column5')}
              value={examSubjectCode}
              onChange={(value) => onSetSubject(value)}
            >
              {res?.data.map((item) => (
                <Select.Option
                  key={item.examSubjectCode}
                  value={item.examSubjectCode}
                >
                  {item.subjectName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </div>
      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={quizData}
          pagination={{
            pageSize: 99999,
          }}
          rowKey="quizCode"
          summary={(record) => {
            const totalScore = getTotal([...record]);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>{t('cbt.test.column22')}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={1}>
                  <div>{totalScore}</div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }}
        />
      </div>

    </>
  );
};

export default TestQuiz;
